import { useState, useMemo, useRef } from 'react';
import { CoreBox } from '@youscience/core';
import {
  FormActionsStatus,
  FormNames,
  EarlyDecisionAgreementSchema,
  EarlyDecisionAgreementDocument,
} from '@youscience/commonapp-service-common';

import { FormState } from '@constants/commonApp/types';

import useRecommenderWorkspaceStore from '@store/RecommenderWorkspaceStore';
import useNotificationStore from '@store/NotificationStore';

import { TabSections } from '@interfaces/recommenderWorkspace';

import { useUserProfile } from '@hooks/useUserProfile';

import FormHeader from '../../common/FormHeader';
import FormFooter from '../../common/FormFooter';
import ReviewPdf, { ReviewTypes } from '../../common/ReviewPdf';
import {
  checkCounselorProfileValidity,
  checkFormValidity,
  PREVIEW_ERROR_NOTIFICATION,
  REVIEW_NOT_READY_ERROR_NOTIFICATION,
  onSaveErrorNotification,
  onSaveSuccessNotification,
  onSubmitSuccessNotification,
  onUnsubmitSuccessNotification,
} from '../../common/formUtils';
import useRecommenderStore from '@store/RecommenderStore';
import Submissions from '../../common/Submissions';

import { sxStyles } from './EarlyDecisionAgreement.styles';
import { useApplicantForm } from '@pages/StudentDetailsPage/hooks/useApplicantForm';
import { FormAvailabilityStatus, FormCompletionStatus } from '../../common/enums';
import { formatDateWithFallback } from '@utils/formatter';
import Agreement from './components/Agreement';
import { AgreementFields } from './components/Agreement/Agreement';

export type EarlyDecisionAgreementFields = Partial<EarlyDecisionAgreementDocument[TabSections.Agreement]>;

interface EarlyDecisionAgreementProps {
  formName: FormNames.EARLY_DECISION | FormNames.EARLY_DECISION2;
}

export const EarlyDecisionAgreement = (props: EarlyDecisionAgreementProps) => {
  const { formName } = props;
  const { notify } = useNotificationStore((state) => state);
  const { recommenderProfile } = useRecommenderStore((store) => store);
  const { userId } = useUserProfile();
  const earlyDecision = useApplicantForm<EarlyDecisionAgreementDocument & { memberId: number }>(formName);
  const initialFormState = useMemo(
    () => ({
      [TabSections.Agreement]: earlyDecision.content?.agreement,
    }),
    [earlyDecision.content],
  );
  const form = useRef(initialFormState);
  const [formErrors, setFormErrors] = useState({});
  const [isExtended, setIsExtended] = useState(true);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [reviewType, setReviewType] = useState<ReviewTypes>(ReviewTypes.PREVIEW_FORM);

  const earlyDecision2Value = formName === FormNames.EARLY_DECISION2 ? '2' : '';
  const isSubmitDisabled = earlyDecision.metadata.availabilityStatus !== FormAvailabilityStatus.AVAILABLE;
  const isSubmitted =
    earlyDecision.metadata.completionStatus === FormCompletionStatus.SUBMITTED ||
    earlyDecision.metadata.completionStatus === FormCompletionStatus.DOWNLOADED;
  const { applicantId, recommenderId } = earlyDecision.metadata;

  const onFormChangeHandler =
    <K,>(section: TabSections) =>
    (sectionFormState: FormState<K>) => {
      form.current = { ...form.current, [section]: sectionFormState.data };
    };

  const validateForm = () => {
    const formValid = checkFormValidity(form.current, EarlyDecisionAgreementSchema);
    const profileValid = checkCounselorProfileValidity(recommenderProfile);

    return {
      form: !formValid,
      recommenderProfile: !profileValid.personal,
      schoolProfile: !profileValid.school,
    };
  };

  const handleSaveForLater = async (isHideNotification?: boolean) => {
    await earlyDecision.save({ ...form.current, applicantId, recommenderId });

    if (isHideNotification) return;

    earlyDecision.isError ? onSaveErrorNotification() : onSaveSuccessNotification(formName);
  };

  const previewForm = async () => {
    if (isSubmitted) {
      setIsPreviewOpen(true);
      await earlyDecision.getSubmitted({ applicantId, recommenderId });
    } else {
      await handleSaveForLater(true);

      const formErrors = validateForm();
      setFormErrors(formErrors);
      setIsPreviewOpen(true);
      const hasFormErrors = Object.values(formErrors).some(Boolean);

      if (!hasFormErrors) {
        await earlyDecision.preview({
          ...form.current,
          applicantId,
          recommenderId,
          memberId: earlyDecision.metadata?.members?.[0]?.memberId,
        });
      }
    }

    useRecommenderWorkspaceStore.subscribe((state) => {
      if (state.isError) {
        notify(isSubmitted ? REVIEW_NOT_READY_ERROR_NOTIFICATION : PREVIEW_ERROR_NOTIFICATION);
        setIsPreviewOpen(false);
      }
    });
  };

  const submitForm = async (signature: string, signedDate: string) => {
    await earlyDecision.submit({
      applicantId,
      recommenderId,
      memberId: earlyDecision.metadata?.members?.[0]?.memberId,
      counselorApproval: { isReviewed: true, isAffirmed: true, signature, signedDate, userId: userId as string },
    });

    earlyDecision.isError ? onSaveErrorNotification() : onSubmitSuccessNotification(formName);
  };

  const handleOnSubmit = () => {
    setReviewType(ReviewTypes.REVIEW_AND_SUBMIT);
    void previewForm();
  };

  const handleOnPreview = async () => {
    setReviewType(isSubmitted ? ReviewTypes.REVIEW_FORM : ReviewTypes.PREVIEW_FORM);
    void previewForm();
  };

  const handleOnUnsubmit = async () => {
    await earlyDecision.unsubmit({ applicantId, recommenderId });

    earlyDecision.isError ? onSaveErrorNotification() : onUnsubmitSuccessNotification(formName);
  };

  if (!earlyDecision.isReady) {
    return null;
  }

  return (
    <CoreBox sx={sxStyles.pageWrap}>
      <FormHeader
        name={`Early decision agreement ${earlyDecision2Value}`}
        tooltipTitle={`This form goes only to the college where your student is applying Early decision ${earlyDecision2Value}. Students, parents, and counselors all read and sign the Early decision agreement ${earlyDecision2Value}.`}
        deadline={formatDateWithFallback(earlyDecision.metadata.deadline)}
        status={earlyDecision.metadata.completionStatus || FormActionsStatus.NEW}
        collegeName={earlyDecision.metadata?.members?.[0]?.memberName}
        onPreviewClick={handleOnPreview}
        onSaveForLaterClick={handleSaveForLater}
        onSubmitClick={handleOnSubmit}
        onUnsubmitClick={handleOnUnsubmit}
        areAllActionsDisabled={earlyDecision.isSavingForm}
        isSubmitDisabled={isSubmitDisabled}
        isFormSumbitted={isSubmitted}
        formName={formName}
      />

      {isSubmitted ? (
        <Submissions formName={formName} onSubmit={handleOnSubmit} />
      ) : (
        <>
          {' '}
          <Agreement
            agreement={earlyDecision.content?.agreement}
            isExtended={isExtended}
            onExpand={() => setIsExtended(!isExtended)}
            onFormChange={onFormChangeHandler<AgreementFields>(TabSections.Agreement)}
            isSaved={earlyDecision.sectionIsSaved.agreement}
          />
        </>
      )}

      <FormFooter
        onPreviewClick={handleOnPreview}
        onSaveForLaterClick={handleSaveForLater}
        onSubmitClick={handleOnSubmit}
        onUnsubmitClick={handleOnUnsubmit}
        isFormSumbitted={isSubmitted}
        formName={formName}
      />

      {isPreviewOpen ? (
        <ReviewPdf
          title="Early Decision Agreement"
          subtitle="Please review the PDF preview of your form. Scroll to the bottom if necessary to confirm and continue."
          isOpen={isPreviewOpen}
          isLoading={earlyDecision.isLoadingPreview}
          reviewType={reviewType}
          pdf={earlyDecision.previewPdfURL}
          onClose={() => setIsPreviewOpen(false)}
          onContinue={(signature?: string, date?: string) => {
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            if (reviewType === ReviewTypes.REVIEW_AND_SUBMIT) submitForm(signature!, date!);
          }}
          errors={formErrors}
        />
      ) : null}
    </CoreBox>
  );
};
